import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from "@lib/orpc-client";
import * as Sentry from "@sentry/react";

export type ActivityResponse = Awaited<
  ReturnType<typeof api.walletV2.activities.list>
>;
export type ActivityItem = ActivityResponse["result"][number];

type ActivityCache = {
  [page: number]: {
    data: ActivityItem[];
    lastUpdated: number;
  };
};

type ActivityState = {
  cache: ActivityCache;
  lastPage: number;
  currentPage: number;
  isReachingEnd: boolean;
  error: Error | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  // Actions
  fetchActivities: (page: number, isLoadMore?: boolean, silentRefresh?: boolean) => Promise<boolean>;
  clearCache: () => void;
  invalidateCache: () => void;
  setCurrentPage: (page: number) => void;
};

const CACHE_EXPIRY_TIME = 1 * 60 * 1000; // 1 minute in milliseconds

export const useActivityStore = create<ActivityState>()(persist((set, get) => {
  return {
    cache: {},
    lastPage: 1,
    currentPage: 1,
    isReachingEnd: false,
    error: null,
    isLoading: false,
    isLoadingMore: false,

    fetchActivities: async (page: number, isLoadMore = false, silentRefresh = false) => {
      const { cache } = get();
      const now = Date.now();
      
      // Check if we have valid cached data for this page
      const cachedPage = cache[page];
      const isCacheValid = cachedPage && 
        cachedPage.lastUpdated && 
        now - cachedPage.lastUpdated < CACHE_EXPIRY_TIME;

      // Core SWR behavior - only show loading state if we don't have valid cache
      // and it's not a silent refresh
      if (!isCacheValid && !silentRefresh) {
        set({ 
          ...(isLoadMore ? { isLoadingMore: true } : { isLoading: true })
        });
      }
      
      // Always fetch fresh data regardless of cache validity

      try {
        const response = await api.walletV2.activities.list({
          sort_by: '-created_at',
          size: 25,
          page
        });
        
        // Update the cache with fresh data
        set((state: ActivityState) => {
          // Prepare new cache data
          const newCache = { ...state.cache };
          
          // Add new page data to cache
          newCache[page] = {
            data: response.result,
            lastUpdated: now
          };
          
          return {
            cache: newCache,
            lastPage: response.last_page,
            isReachingEnd: page >= response.last_page,
            isLoading: false,
            isLoadingMore: false,
            error: null
          };
        });
        
        return true;
      } catch (error) {
        const err = error as Error;
        set({ error: err, isLoading: false, isLoadingMore: false });
        Sentry.captureException(err);
        return false;
      }
    },

    clearCache: () => set({ cache: {}, currentPage: 1, lastPage: 1, isReachingEnd: false }),
    
    invalidateCache: () => set({ cache: {} }),
    
    setCurrentPage: (page: number) => set({ currentPage: page })
  };
}, {
  name: 'activity-cache',
  partialize: (state) => ({ 
    cache: state.cache, 
    lastPage: state.lastPage,
    currentPage: state.currentPage
  })
}));
